import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { tenantApiFetch } from './tenantApi';

export default function TenantPermissionsPage() {
  const navigate = useNavigate();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tenantApiFetch('/api/tenant/permissions');
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load permissions');
      setPermissions(json.permissions || []);
    } catch (e) {
      setError(e.message);
      setPermissions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const p of permissions) {
      const key = p.module_name || 'Other';
      if (!m.has(key)) m.set(key, []);
      m.get(key).push(p);
    }
    return Array.from(m.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [permissions]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Permissions catalog
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Read-only list of permission codes you can assign to custom roles. Definitions are managed on the platform.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {grouped.map(([mod, rows]) => (
        <Accordion key={mod} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>
              {mod}
              <Chip size="small" label={rows.length} sx={{ ml: 1 }} />
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <List dense disablePadding>
              {rows.map((p) => (
                <ListItem key={p.id} alignItems="flex-start" sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2">{p.permission_name}</Typography>
                        <Typography variant="caption" component="code">
                          {p.permission_code}
                        </Typography>
                        {p.is_system_permission && <Chip size="small" label="System" />}
                      </Box>
                    }
                    secondary={
                      <>
                        {p.description && (
                          <Typography variant="body2" color="text.secondary" component="span" display="block">
                            {p.description}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" component="span">
                          Action: {p.action_name || '—'}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}

      {permissions.length === 0 && !error && (
        <Paper sx={{ p: 3 }}>
          <Typography color="text.secondary">No permissions returned.</Typography>
        </Paper>
      )}

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          To assign permissions, use{' '}
          <Typography
            component="button"
            variant="caption"
            onClick={() => navigate('/tenant/roles')}
            sx={{ cursor: 'pointer', textDecoration: 'underline', border: 'none', background: 'none', p: 0 }}
          >
            Roles
          </Typography>
          .
        </Typography>
      </Box>
    </Box>
  );
}
